import React, { useState, useRef, useEffect, useId } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import adMediaManager from '../utils/adMediaManager';

const AudioAdPlayer = ({ audioUrl, autoPlay = false, onPlay }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);
  
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const uniqueId = useId();
  const isNative = Capacitor.isNativePlatform();

  // Register with global media manager
  useEffect(() => {
    adMediaManager.registerAudio(uniqueId, {
      onForceStop: () => {
        console.log(`🛑 Force stopping audio: ${uniqueId}`);
        setIsPlaying(false);
      }
    });

    return () => {
      adMediaManager.unregisterAudio(uniqueId);
    };
  }, [uniqueId]);

  // Intersection Observer to detect visibility
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const nowVisible = entry.isIntersecting;
          setIsVisible(nowVisible);
          
          // If the ad is no longer visible and is playing, pause it
          if (!nowVisible && isPlaying) {
            console.log(`👁️ Audio ad ${uniqueId} left viewport - pausing`);
            setIsPlaying(false);
            adMediaManager.notifyAudioStopped(uniqueId);
          }
        });
      },
      {
        threshold: 0.6, // 60% visible to trigger
        rootMargin: '0px'
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [isPlaying, uniqueId]);

  // Autoplay ONLY when visible on screen (for native iOS/Android)
  useEffect(() => {
    if (!isNative || !autoPlay || !isReady) return;
    
    // Only autoplay if visible and not already playing
    if (isVisible && !isPlaying) {
      // Request permission from manager (will stop other audio ads)
      const canPlay = adMediaManager.requestAudioPlay(uniqueId);
      if (canPlay) {
        const audio = audioRef.current;
        if (audio) {
          audio.play().then(() => {
            setIsPlaying(true);
            if (onPlay) onPlay();
            console.log(`▶️ Audio ad ${uniqueId} started (visible)`);
          }).catch(err => {
            console.log('Autoplay blocked:', err);
            adMediaManager.notifyAudioStopped(uniqueId);
          });
        }
      }
    }
  }, [isVisible, isNative, autoPlay, isReady, isPlaying, uniqueId, onPlay]);

  // Audio element setup
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsReady(true);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      adMediaManager.notifyAudioStopped(uniqueId);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [uniqueId]);

  // Handle play/pause state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.play().catch(err => {
        console.log('Play error:', err);
        setIsPlaying(false);
      });
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  const togglePlay = () => {
    if (!isPlaying) {
      // Request to play (will stop other audio)
      adMediaManager.requestAudioPlay(uniqueId);
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
      adMediaManager.notifyAudioStopped(uniqueId);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Generate waveform bars
  const bars = Array.from({ length: 40 }, (_, i) => {
    const baseHeight = 20 + Math.sin(i * 0.5) * 15;
    const animatedHeight = isPlaying 
      ? baseHeight + Math.sin(currentTime * 5 + i * 0.3) * 10 
      : baseHeight;
    const isPast = (i / 40) * 100 < progress;
    
    return {
      height: Math.max(10, animatedHeight),
      isPast
    };
  });

  return (
    <div 
      ref={containerRef}
      className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 p-6 rounded-xl border border-violet-500/20"
    >
      <audio 
        ref={audioRef} 
        src={audioUrl} 
        preload="metadata"
      />
      
      <div className="flex items-center space-x-4">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          className={`flex-shrink-0 w-14 h-14 flex items-center justify-center bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 rounded-full transition-all shadow-lg hover:shadow-violet-500/50 ${
            !isPlaying ? 'animate-pulse' : ''
          }`}
          title={isPlaying ? "Pause" : "Écouter la publicité"}
        >
          {isPlaying ? (
            <Pause className="w-6 h-6 text-white" fill="white" />
          ) : (
            <Play className="w-6 h-6 text-white ml-1" fill="white" />
          )}
        </button>

        {/* Waveform */}
        <div className="flex-1 relative">
          <div className="flex items-center justify-center space-x-1 h-16">
            {bars.map((bar, i) => (
              <div
                key={i}
                className="flex-1 rounded-full transition-all duration-150"
                style={{
                  height: `${bar.height}%`,
                  backgroundColor: bar.isPast 
                    ? 'rgb(139, 92, 246)' 
                    : 'rgba(139, 92, 246, 0.3)',
                  minWidth: '2px',
                  maxWidth: '4px'
                }}
              />
            ))}
          </div>
          
          {/* Time display */}
          <div className="flex justify-between items-center mt-2 text-xs text-gray-400">
            <span>{formatTime(currentTime)}</span>
            <div className="flex items-center space-x-1">
              <Volume2 className="w-3 h-3" />
              <span>Audio sponsorisé</span>
            </div>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioAdPlayer;
